import React, { useState, useMemo } from 'react'

import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import ExpandedListHR from 'components/styled/ExpandedListHR'
import LotsTable from 'components/order_history/LotsTable'

// local components
import StatusHistoryDisplay from './components/StatusHistoryDisplay'
import Notes from './components/Notes'
import ShowMoreContent from './components/ShowMoreContent'
import OrderProgress from 'components/order_history/OrderProgress'
import useOrderStatusHistory from 'hooks/orders/useOrderStatusHistory'

// Skeleton loader for status history
function StatusHistorySkeleton() {
  return (
    <Grid container sx={{ mb: 24 }}>
      <Grid item xs={12}>
        <Typography fontWeight="medium" fontSize={14}>
          Status History
        </Typography>
        <ExpandedListHR />
        <Box sx={{ ml: 6 }}>
          <Box sx={{ display: 'flex' }}>
            {[1, 2, 3, 4].map((index) => (
              <Box key={`skeleton-${index}`} sx={{ display: 'flex' }}>
                <Box sx={{ position: 'relative' }}>
                  <Skeleton
                    variant="circular"
                    width={48}
                    height={48}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ position: 'absolute', width: '96px' }}>
                    <Box
                      sx={{
                        transform: 'translateX(-24px)',
                        textAlign: 'center',
                      }}
                    >
                      <Skeleton variant="text" width={80} height={20} />
                      <Skeleton variant="text" width={60} height={16} />
                    </Box>
                  </Box>
                </Box>
                {index < 4 && (
                  <Box
                    sx={{
                      width: '100px',
                      height: '2px',
                      backgroundColor: 'lightgray.main',
                      marginTop: 'auto',
                      marginBottom: 'auto',
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

function OrderHistoryExpandedList({
  children,
  expanded,
  rowData,
  columns,
  statusTitle = '',
  getDataDisplay,
  visibleColumns,
  collapseDetails,
}) {
  const { notes } = rowData?._embedded ?? {}
  const gridWidth = notes?.length > 0 ? 12 : 6
  const { statusHistory: _statusHistory, loading: statusHistoryLoading } =
    useOrderStatusHistory({
      orderId: expanded && rowData.order_id,
    })

  const [open, setOpen] = useState(!collapseDetails)

  const statusHistory = useMemo(() => {
    if (!Array.isArray(_statusHistory)) return []

    const localStatusHistory = []
    let currentStatus = null

    _statusHistory.forEach((status) => {
      if (rowData['status'].status === status.status) {
        if (currentStatus) currentStatus.current = false

        currentStatus = {
          ...status,
          current: true,
        }
        localStatusHistory.push(currentStatus)
      } else {
        localStatusHistory.push({
          ...status,
          current: false,
        })
      }
    })

    if (!currentStatus && localStatusHistory.length > 0) {
      localStatusHistory.at(-1).current = true
    }

    return localStatusHistory
  }, [_statusHistory, rowData])

  return (
    <Collapse
      in={expanded}
      sx={{
        textAlign: 'left',
        px: 11,
        pt: 2,
        mt: 0,
        '& .MuiCollapse-entered': {
          mt: 4,
        },
      }}
    >
      {statusTitle && (
        <Typography fontWeight="bold" fontSize={14}>
          {statusTitle}
        </Typography>
      )}

      {rowData?.status?.status?.toLowerCase() === 'processing' && (
        <OrderProgress order={rowData} label="Processing Progress" />
      )}

      {expanded && (
        <>
          {statusHistoryLoading ? (
            <StatusHistorySkeleton />
          ) : (
            statusHistory && (
              <StatusHistoryDisplay
                statusHistory={statusHistory}
                currentStatus={rowData?.status}
              />
            )
          )}
        </>
      )}

      {Boolean(rowData?.lots && expanded) && <LotsTable lots={rowData?.lots} />}

      <Grid container columns={gridWidth} spacing={30}>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography fontWeight="medium" fontSize={14}>
              Details
            </Typography>
            {collapseDetails && (
              <IconButton onClick={() => setOpen(!open)}>
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
          <ExpandedListHR />
          <ShowMoreContent
            rowData={rowData}
            columns={columns}
            open={open}
            getDataDisplay={getDataDisplay}
            visibleColumns={visibleColumns}
          />
        </Grid>
        {notes?.length > 0 && (
          <Grid item xs={6}>
            <Notes notes={notes} />
          </Grid>
        )}
      </Grid>
      {children}
    </Collapse>
  )
}

export default OrderHistoryExpandedList
